import RatingStar from "@/components/app/RatingStar";
import { SiteInfo } from "@/globals";
import { convertCurrencySign, convertPromotionCodeValue, formatSalesQuantity } from "@/js/format/Format";
import { getProductById, getProducts, ProductResponse } from "@/js/repository/ProductRepository";
import { getCategoriesByProducts } from "@/js/repository/CategoryRepository";
import { Separator } from "@radix-ui/react-separator";
import Head from "next/head";
import { useTranslation } from "react-i18next";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { ProductList } from "../products";
import { getCategoryPath, getProductPath, getProductIdFromSlug } from "@/js/utils/stringUtils";
import { AdSenseBanner } from "@/components/app/AdSenseBanner";
import { useIsMobile } from "@/lib/useIsMobile";
import { BadgePercent, ExternalLink, ShieldCheck } from "lucide-react";
import { buildKeywordContent } from "@/lib/seo";

const truncateTitle = (title: string, maxLength: number = 70) => {
  return title.length > maxLength ? title.substring(0, maxLength) + '...' : title;
};

const getFullTitle = (productTitle: string, discountRate: number, siteName: string) => {
  const maxTitleLength = 70;
  const discountRateText = discountRate > 0 ? `[${discountRate.toFixed(0)}% OFF] ` : '';
  return `${truncateTitle(`${discountRateText}${productTitle} | ${siteName}`, maxTitleLength)}`;
};

const TabNavigation = ({ currentSection, onTabClick }: { currentSection: string; onTabClick: (section: string) => void }) => {
  const { t } = useTranslation();
  const tabs = [
    { id: 'description', label: t("productDescription"), controls: 'product-description-section' },
    { id: 'review', label: t("reviews"), controls: 'product-review-section' },
    { id: 'related', label: t("relatedItems"), controls: 'product-related-section' },
  ];
  return (
    <div role="tablist" aria-label={t("productDescription")} className="flex space-x-8 border-b">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          role="tab"
          aria-selected={currentSection === tab.id}
          aria-controls={tab.controls}
          id={`tab-${tab.id}`}
          className={`py-4 font-medium ${currentSection === tab.id ? 'text-[#fd384f] border-b-2 border-[#fd384f]' : 'text-gray-500'}`}
          onClick={() => onTabClick(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
};

export async function getServerSideProps(context: any) {
  const { locale } = context;
  const { slug } = context.params as { slug: string };
  const siteUrl = `https://${context.req.headers.host}`;

  const productId = getProductIdFromSlug(slug);
  if (productId === null) {
    return { notFound: true };
  }

  const categories = await getCategoriesByProducts(locale);
  const product = await getProductById(locale, String(productId));

  if (!product) {
    return { notFound: true };
  }

  const productPath = getProductPath(product.detail.product_id, product.detail.product_title);
  const canonicalSlug = productPath.replace(/^\/product\//, '');
  const decodedSlug = decodeURIComponent(slug);
  if (decodedSlug !== canonicalSlug) {
    const encodedSlug = encodeURIComponent(canonicalSlug);
    return {
      redirect: {
        destination: `/product/${encodedSlug}`,
        permanent: true,
      },
    };
  }

  const relatedProducts = await getProducts(locale, 1, 8, product.detail.second_level_category_id, product.detail.product_id);

  const formattedLastmod = new Date(product.lastmod).toLocaleString(locale ?? 'ko', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return {
    props: { categories, product, relatedProducts, siteUrl, formattedLastmod },
  };
}

export default function ProductSlugPage(props: any) {
  const { locale } = useRouter();
  const { t } = useTranslation();
  const siteUrl = props.siteUrl;
  const isMobile = useIsMobile();
  const descriptionRef = useRef<HTMLDivElement>(null);
  const reviewRef = useRef<HTMLDivElement>(null);
  const relatedRef = useRef<HTMLDivElement>(null);
  const [showFixedButton, setShowFixedButton] = useState(false);
  const isHidden = props.product.hidden;
  const product: ProductResponse = props.product;
  const productPath = getProductPath(product.detail.product_id, product.detail.product_title);

  useEffect(() => {
    const handleScroll = () => {
      if (descriptionRef.current) {
        const descriptionTop = descriptionRef.current.getBoundingClientRect().top;
        const footerElement = document.querySelector('footer');
        const footerTop = footerElement ? footerElement.getBoundingClientRect().top : window.innerHeight;
        setShowFixedButton(descriptionTop < 0 && footerTop > window.innerHeight);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (section: string) => {
    const headerHeight = 64;
    let targetRef: React.RefObject<HTMLDivElement | null> | null = null;
    switch (section) {
      case 'description': targetRef = descriptionRef; break;
      case 'review': targetRef = reviewRef; break;
      case 'related': targetRef = relatedRef; break;
    }
    if (targetRef?.current) {
      const elementPosition = targetRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const sale_price = parseFloat(product.detail.target_sale_price);
  const original_price = parseFloat(product.detail.target_original_price);
  const discount_rate = (original_price - sale_price) / original_price * 100;
  const rating = product.detail.evaluate_rate ? (parseInt(product.detail.evaluate_rate.replace('%', ''), 10) / 20).toFixed(1) : null;
  const promoCodeInfo = product.detail.promo_code_info;
  const promoCode = promoCodeInfo?.promo_code?.trim();
  const description = `${SiteInfo.siteName} ${t("curatedByProsense")} | ${convertCurrencySign(product.detail.target_sale_price_currency, sale_price.toLocaleString(locale))} | ${discount_rate.toFixed(0)}% ${t("discount")} | ${formatSalesQuantity(product.detail.lastest_volume, locale)} ${t("sold")} | ${product.detail.first_level_category_name}, ${product.detail.second_level_category_name} | ${t("shopNow")}`;

  const openExternalLink = (url: string) => {
    const externalWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (externalWindow) {
      externalWindow.opener = null;
    }
  };

  const openProductLink = (p: ProductResponse, isViewMore: boolean = false) => {
    openExternalLink(p.detail.promotion_link);
    if (typeof window !== 'undefined') {
      const gtag = (window as any).gtag;
      if (gtag) {
        gtag('event', isViewMore ? '구매버튼 클릭 (더보기)' : '구매버튼 클릭', {
          product_title: p.detail.product_title,
          product_id: p.detail.product_id,
        });
      }
    }
  };

  return (
    <>
      <Head>
        <title>{getFullTitle(product.detail.product_title, discount_rate, SiteInfo.siteName)}</title>
        <meta name="description" content={description} />
        <meta
          name="keywords"
          content={buildKeywordContent([
            ...SiteInfo.primaryKeywords,
            product.detail.product_title,
            product.detail.first_level_category_name,
            product.detail.second_level_category_name,
            '특가',
            '할인',
            '쿠폰',
          ])}
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${siteUrl}${productPath}`} />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={`${siteUrl}${productPath}`} />
        <meta property="og:title" content={getFullTitle(product.detail.product_title, discount_rate, SiteInfo.siteName)} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={product.detail.product_main_image_url} />
        <meta property="og:site_name" content={SiteInfo.siteName} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={getFullTitle(product.detail.product_title, discount_rate, SiteInfo.siteName)} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={product.detail.product_main_image_url} />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              {
                '@context': 'https://schema.org/',
                '@type': 'Product',
                name: truncateTitle(product.detail.product_title, 70 - (SiteInfo.siteName.length + 3)),
                image: [product.detail.product_main_image_url, ...(product.detail.product_small_image_urls?.string || []).filter((_: string, i: number) => i > 0)],
                description,
                category: `${product.detail.first_level_category_name} > ${product.detail.second_level_category_name}`,
                sku: product.detail.product_id,
                offers: {
                  '@type': 'Offer',
                  priceCurrency: product.detail.target_sale_price_currency,
                  price: sale_price,
                  itemCondition: 'https://schema.org/NewCondition',
                  availability: 'https://schema.org/InStock',
                  url: `${siteUrl}${productPath}`,
                  priceSpecification: {
                    '@type': 'PriceSpecification',
                    price: sale_price,
                    priceCurrency: product.detail.target_sale_price_currency,
                    valueAddedTaxIncluded: true,
                    priceType: 'https://schema.org/SalePrice',
                  },
                },
              },
              null,
              2
            ),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
                { '@type': 'ListItem', position: 2, name: product.detail.first_level_category_name, item: `${siteUrl}${getCategoryPath(product.detail.first_level_category_id, product.detail.first_level_category_name)}` },
                { '@type': 'ListItem', position: 3, name: product.detail.second_level_category_name, item: `${siteUrl}${getCategoryPath(product.detail.second_level_category_id, product.detail.second_level_category_name)}` },
                { '@type': 'ListItem', position: 4, name: truncateTitle(product.detail.product_title, 50) },
              ],
            }, null, 2),
          }}
        />
      </Head>

      <nav aria-label="Breadcrumb" className="mb-4">
        <ol className="flex flex-wrap items-center gap-x-1 text-sm font-semibold text-gray-600" itemScope itemType="https://schema.org/BreadcrumbList">
          <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
            <a itemProp="item" href={getCategoryPath(product.detail.first_level_category_id, product.detail.first_level_category_name)} className="hover:text-[#fd384f]"><span itemProp="name">{product.detail.first_level_category_name}</span></a>
          </li>
          <span className="mx-1 text-gray-400" aria-hidden="true">/</span>
          <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
            <a itemProp="item" href={getCategoryPath(product.detail.second_level_category_id, product.detail.second_level_category_name)} className="hover:text-[#fd384f]"><span itemProp="name">{product.detail.second_level_category_name}</span></a>
          </li>
        </ol>
      </nav>

      <section className="grid gap-6 rounded-lg border border-black/10 bg-white p-4 shadow-sm sm:p-6 lg:grid-cols-[minmax(0,0.95fr)_minmax(360px,0.75fr)]">
        <div>
          <div className="relative overflow-hidden rounded-lg bg-[#f2efe8]">
            {discount_rate > 0 && (
              <span className="absolute left-3 top-3 z-10 inline-flex items-center gap-1 rounded-md bg-[#fd384f] px-3 py-2 text-sm font-black text-white">
                <BadgePercent className="h-4 w-4" />
                -{discount_rate.toFixed(0)}%
              </span>
            )}
            <img className="aspect-square w-full object-contain p-3" src={product.detail.product_main_image_url} alt={truncateTitle(product.detail.product_title, 100)} width={800} height={800} loading="lazy" />
          </div>
        </div>

        <div className="flex min-w-0 flex-col">
          <p className="text-xs font-black uppercase tracking-normal text-[#fd384f]">{t("curatedByProsense")}</p>
          <h1 className="mt-2 break-words text-2xl font-black leading-tight text-[#101820] sm:text-3xl">{product.detail.product_title}</h1>

          <div className="mt-5 rounded-lg bg-[#f7f4ef] p-4">
            <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
              <span className="text-3xl font-black leading-none text-[#fd384f]">{convertCurrencySign(product.detail.target_sale_price_currency, sale_price.toLocaleString(locale))}</span>
              {original_price > sale_price && (
                <span className="text-sm font-semibold leading-none text-gray-400 line-through">{convertCurrencySign(product.detail.target_sale_price_currency, original_price.toLocaleString(locale))}</span>
              )}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm font-semibold text-gray-600">
              {rating && <RatingStar rating={Number(rating)} />}
              {rating && <span>{rating}</span>}
              {rating && <Separator className="h-[12px] border-l border-gray-300" />}
              <span>{formatSalesQuantity(product.detail.lastest_volume, locale)} {t("sold")}</span>
            </div>
          </div>

          {!isHidden && (
            <button className="mt-4 inline-flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-[#fd384f] text-base font-black text-white transition hover:bg-[#101820]" onClick={() => openProductLink(product)} title={t("shopNow")}>
              {t("shopNow")}
              <ExternalLink className="h-5 w-5" />
            </button>
          )}
          {isHidden && <div className="mt-4 text-xl font-bold text-red-500">{t("productHidden")}</div>}

          <div className="mt-4 grid gap-2 text-sm text-gray-600">
            <div className="flex gap-2 rounded-lg border border-black/10 p-3">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#0f766e]" />
              <span>{t("affiliateDisclosure")}</span>
            </div>
            <div className="rounded-lg border border-black/10 p-3">
              <div className="font-bold text-[#101820]">{t("lastUpdated")}: {props.formattedLastmod}</div>
              <div className="mt-1">{product.detail.first_level_category_name} / {product.detail.second_level_category_name}</div>
            </div>
          </div>

          <section className="mt-6 rounded-lg border border-black/10 bg-white p-4" aria-label={t("productCheckTitle")}>
            <h2 className="text-base font-black text-[#101820]">{t("productCheckTitle")}</h2>
            <ul className="mt-3 grid gap-2 text-sm leading-6 text-gray-600">
              <li>{t("productCheckPrice")}</li>
              <li>{t("productCheckReviews")}</li>
              <li>{t("productCheckDisclosure")}</li>
            </ul>
          </section>

          {promoCode && promoCodeInfo && (
            <div className="mt-5">
              <div className="text-lg font-black">{t("promoCode")}</div>
              <div className="mt-2 rounded-lg bg-[#fff1f2] p-3 text-sm">
                <div>
                  <span className="text-base font-bold align-middle my-auto underline">{promoCode}</span>
                  <button className="ml-3 text-md align-middle my-auto text-red-500" onClick={() => { navigator.clipboard.writeText(promoCode); alert(t("copiedPromoCodeMessage")); }} title={t("copy")}>{t("copy")}</button>
                </div>
                {promoCodeInfo.code_value && <div className="mt-1">{convertPromotionCodeValue(promoCodeInfo.code_value)}</div>}
                {promoCodeInfo.code_availabletime_end && <div className="mt-1">{t("expires")} {new Date(promoCodeInfo.code_availabletime_end).toLocaleString(locale)}</div>}
              </div>
            </div>
          )}
        </div>
      </section>

      {showFixedButton && !isHidden && (
        <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-black/10 bg-white/95 px-4 py-2 shadow-lg backdrop-blur-sm">
          <div className="max-w-7xl mx-auto">
            <button className="flex h-[44px] w-full items-center justify-center gap-2 rounded-lg bg-[#fd384f] text-base font-black text-white" onClick={() => openProductLink(product)} title={t("shopNow")}>
              {t("shopNow")}
              <ExternalLink className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      <div className="my-6">
        <AdSenseBanner id="product-content-mid" slot="4220411723" height={isMobile ? 100 : 120} />
      </div>

      <Separator className="my-12 border-t" />

      <div id="product-description-section" ref={descriptionRef} className="pt-8" role="tabpanel" aria-labelledby="tab-description">
        <div className="mb-8"><TabNavigation currentSection="description" onTabClick={scrollToSection} /></div>
        {product.detail.product_video_url && (
          <video width="100%" className="mt-2 mb-24 max-h-[693px]" controls>
            <source src={product.detail.product_video_url} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )}
        <div className="mt-2">
          {product.detail.product_small_image_urls?.string?.map((image: string, index: number) => {
            if (index === 0) return null;
            return <img src={image} alt={`${truncateTitle(product.detail.product_title, 50)} - ${index}`} width={800} height={800} className="w-full" key={`image-${index}`} loading="lazy" />;
          })}
        </div>
        {!isHidden && <div className="mt-12 text-center"><button className="rounded-lg bg-[#101820] px-14 py-3 text-md font-bold text-white transition hover:bg-[#fd384f]" onClick={() => openProductLink(product, true)} title={t("shopNow")}>{t("shopNow")}</button></div>}
      </div>

      <div className="my-6">
        <AdSenseBanner id="product-content-after-desc" slot="4220411723" height={isMobile ? 100 : 120} />
      </div>

      <div id="product-review-section" ref={reviewRef} className="pt-8" role="tabpanel" aria-labelledby="tab-review">
        <div className="mb-8"><TabNavigation currentSection="review" onTabClick={scrollToSection} /></div>
        <div className="text-center py-12">
          <div className="flex items-center justify-center mb-6">
            {rating && <><RatingStar rating={Number(rating)} /><span className="ml-2 text-lg font-medium">{rating}</span><Separator className="h-[16px] align-middle my-auto mx-2 border-l border-gray-300" /><span className="text-gray-500">{formatSalesQuantity(product.detail.lastest_volume, locale)} {t("sold")}</span></>}
          </div>
          {!isHidden && <button className="rounded-lg bg-[#fd384f] px-8 py-3 text-md font-bold text-white transition-colors hover:bg-[#101820]" onClick={() => openExternalLink(`${product.detail.promotion_link}#tab-reviews`)}>{t("checkReviewsOnAliExpress")}</button>}
        </div>
      </div>

      <div id="product-related-section" ref={relatedRef} className="pt-8" role="tabpanel" aria-labelledby="tab-related">
        <div className="mb-8"><TabNavigation currentSection="related" onTabClick={scrollToSection} /></div>
        <ProductList products={props.relatedProducts} />
      </div>

      <div className="my-6">
        <AdSenseBanner id="product-content-bottom" slot="4220411723" height={isMobile ? 180 : 240} />
      </div>
    </>
  );
}
